import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/layout/AdminLayout';
import { useAuth, API } from '@/App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import axios from 'axios';
import { Users, Search, AlertTriangle, MessageSquare, Upload, Clock, ChevronRight, Eye } from 'lucide-react';

const signalIcons = {
  missing_checkin: { icon: Clock, color: 'text-amber-400', label: 'No check-in today' },
  unresolved_support: { icon: MessageSquare, color: 'text-red-400', label: 'Open support' },
  upload_pending_review: { icon: Upload, color: 'text-blue-400', label: 'Upload pending' },
};

export default function AssignedSubscribers() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('priority');

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/my/assigned-subscribers?sort_by=${sortBy}`);
        setSubscribers(res.data);
      } catch (err) {
        console.error('Fetch error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [sortBy]);

  const filtered = subscribers.filter(s =>
    s.name?.toLowerCase().includes(search.toLowerCase()) ||
    s.email?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-['Outfit'] font-bold text-adm-primary text-adm-primary flex items-center gap-3" data-testid="assigned-title">
            <Users className="w-7 h-7 text-[#C89B7B]" />
            Assigned Subscribers
          </h1>
          <p className="text-adm-tertiary text-adm-primary/60 mt-1">Your active subscriber caseload</p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-adm-tertiary text-adm-primary/40" />
            <Input
              placeholder="Search subscribers..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9 bg-adm-surface bg-adm-surface border-white/[0.06] border-white/[0.06]"
              data-testid="assigned-search"
            />
          </div>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-full sm:w-[180px] bg-adm-surface bg-adm-surface border-white/[0.06] border-white/[0.06]" data-testid="assigned-sort">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-adm-surface bg-adm-surface border-white/[0.06] border-white/[0.06]">
              <SelectItem value="priority">Priority</SelectItem>
              <SelectItem value="name">Name</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="text-center py-12 text-adm-tertiary text-adm-primary/40">Loading...</div>
        ) : filtered.length === 0 ? (
          <Card className="bg-adm-surface border-white/[0.06]">
            <CardContent className="py-12 text-center">
              <Users className="w-12 h-12 text-slate-300 text-adm-primary/15 mx-auto mb-4" />
              <p className="text-adm-tertiary text-adm-primary/50">
                {subscribers.length === 0 ? 'No subscribers assigned to you yet' : 'No matching subscribers found'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map(sub => (
              <Card key={sub.id} className="bg-adm-surface border-white/[0.06] hover:border-[#C89B7B]/30 transition-colors cursor-pointer group"
                onClick={() => navigate(`/admin/care-dashboard/${sub.id}`)} data-testid={`subscriber-card-${sub.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="font-medium text-adm-primary text-adm-primary">{sub.name}</p>
                      <p className="text-xs text-adm-tertiary text-adm-primary/40">{sub.email}</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-300 text-adm-primary/20 group-hover:text-[#C89B7B] transition-colors" />
                  </div>

                  <div className="flex items-center gap-2 mb-3">
                    <Badge className={`text-xs ${
                      sub.status === 'active_phase' ? 'bg-[#4E7A6B]/20 text-[#4E7A6B]' :
                      sub.status === 'onboarding_pending' ? 'bg-amber-500/20 text-amber-400' :
                      'bg-adm-surface/10 text-white/50'
                    }`}>
                      {sub.status?.replace(/_/g, ' ') || 'pending'}
                    </Badge>
                    {sub.has_checkin_today && (
                      <Badge className="bg-[#4E7A6B]/10 text-[#4E7A6B] text-xs">checked in</Badge>
                    )}
                  </div>

                  {sub.priority_signals?.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {sub.priority_signals.map(sig => {
                        const info = signalIcons[sig];
                        if (!info) return null;
                        const Icon = info.icon;
                        return (
                          <div key={sig} className="flex items-center gap-1">
                            <Icon className={`w-3.5 h-3.5 ${info.color}`} />
                            <span className={`text-xs ${info.color}`}>{info.label}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
